import * as d3 from 'd3'
import React, { useState, useEffect } from 'react'

import { ZoomHandler } from 'plugin-codaf-risk-ext-bowtie/generic'
import { d3PromiseSelect } from 'plugin-codaf-risk-ext-bowtie/utils'
import { settingsZoomLevel } from 'plugin-codaf-risk-ext-bowtie/utils/constants'

import { IBarrierTypes, IEditorBarrier, IEditorEscalation } from '../../BarrierEditor'
import { BarrierToEdit } from './components'
import { IBarrierTypeIcon } from 'plugin-codaf-risk-ext-bowtie/types'

interface Props {
    width: number
    height: number
    barrierData: IEditorBarrier
    editingItem: IEditorBarrier | IEditorEscalation
    handleEditBarrier?: (k: IEditorBarrier | IEditorEscalation, type: IBarrierTypes) => void
    handleBarrierChange(): void
    handleGetRecursiveSearch: (barrierRef: IEditorBarrier) => IEditorBarrier[]
    handleGetRecursiveSearchFromEscalation: (ecalationRef: IEditorEscalation) => IEditorEscalation[]
    barrierTypeIconMap: Map<number, IBarrierTypeIcon>
}

export const BarrierEditorSvg: React.FC<Props> = ({
    height,
    width,
    barrierData,
    editingItem,
    handleEditBarrier,
    handleBarrierChange,
    handleGetRecursiveSearch,
    handleGetRecursiveSearchFromEscalation,
    barrierTypeIconMap
}) => {
    const [vector3, setVector3] = useState({
        x: width / 2,
        y: height / 2 + 30,
        k: 1.5
    })

    const [bowtieSVG, setBowtieSVG] = useState<d3.Selection<SVGSVGElement, unknown, HTMLElement, any>>(null) // eslint-disable-line @typescript-eslint/no-explicit-any
    const [zoomBehaviour, setZoomBehaviour] = useState<d3.ZoomBehavior<Element, unknown>>(null)

    useEffect(() => {
        initd3Listeners() // eslint-disable-line @typescript-eslint/no-use-before-define
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    const initd3Listeners = async (): Promise<void> => {
        const svg = await d3PromiseSelect<SVGSVGElement, unknown>('#bowtie-svg')

        const zoomHandler = d3
            .zoom()
            .on('zoom', () => setVector3(d3.event.transform))
            .scaleExtent([settingsZoomLevel[0], settingsZoomLevel[1]])
        setTimeout(() => {
            zoomHandler(svg)
            svg.on('dblclick.zoom', null)

            svg.call(zoomHandler.transform, d3.zoomIdentity.translate(width / 2, height / 2 + 30).scale(1.5))
            setZoomBehaviour(() => zoomHandler)
            setBowtieSVG(svg)
        }, 400)
    }

    return (
        <>
            <svg id="bowtie-svg" width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
                <defs>
                    <filter id="blur" x="-50" y="-50" width="200" height="200">
                        <feGaussianBlur result="blurOut" in="offOut" stdDeviation="5" />
                    </filter>
                </defs>
                <g id="bowtie-container" transform={`translate(${vector3.x} ${vector3.y}) scale(${vector3.k})`}>
                    <BarrierToEdit
                        x={0}
                        y={0}
                        barrierData={barrierData}
                        zoomScale={vector3.k}
                        primaryBarrier={true}
                        handleEditBarrier={handleEditBarrier}
                        handleBarrierChange={handleBarrierChange}
                        handleGetRecursiveSearch={handleGetRecursiveSearch}
                        handleGetRecursiveSearchFromEscalation={handleGetRecursiveSearchFromEscalation}
                        beforeBrothersEscalationsLength={0}
                        editingItem={editingItem}
                        selectedMethod={barrierData?.method}
                        barrierTypeIconMap={barrierTypeIconMap}
                    />
                </g>
            </svg>
            <ZoomHandler scale={vector3.k} zoomBehaviour={zoomBehaviour} target={bowtieSVG} />
        </>
    )
}
